import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Card, Chip, CircularProgress, Paper, Stack, Typography
} from "@mui/material";
import {
    AutoGraphRounded, CalendarMonthRounded, QueryStatsRounded, TodayRounded,
    TrendingUpRounded, VisibilityRounded
} from "@mui/icons-material";
import {
    Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer,
    Tooltip, XAxis, YAxis
} from "recharts";
import viewCounterController from "../../../../../Api/viewCountController";

type DataMap = Record<string, Array<{ name: string; pv: number }>>;
type ViewMode = 'months' | 'days' | 'hours';

interface ChartsProps {
    dashboard?: boolean;
}

const number = (value?: number | null) => new Intl.NumberFormat('pl-PL').format(value || 0);

const Charts: FC<ChartsProps> = ({ dashboard = false }) => {
    const [monthlyByYear, setMonthlyByYear] = useState<DataMap>({});
    const [dailyByYear, setDailyByYear] = useState<DataMap>({});
    const [hourlyByYear, setHourlyByYear] = useState<DataMap>({});
    const [weekly, setWeekly] = useState(0);
    const [monthly, setMonthly] = useState(0);
    const [total, setTotal] = useState(0);
    const [year, setYear] = useState('');
    const [mode, setMode] = useState<ViewMode>('months');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        const res = await viewCounterController.getStatisticsData();
        if (!res.ok || !res.views) {
            setError(res.message || 'Nie udało się pobrać statystyk.');
            setLoading(false);
            return;
        }
        const views = res.views as unknown as DataMap;
        const years = Object.keys(views).sort((a, b) => Number(b) - Number(a));
        setMonthlyByYear(views);
        setDailyByYear((res.dailyStatsPerYear || {}) as DataMap);
        setHourlyByYear((res.hourlyStatsPerYear || {}) as DataMap);
        setWeekly(Number(res.weeklyViews || 0));
        setMonthly(Number(res.monthlyViews || 0));
        setTotal(Number(res.viewCount || 0));
        setYear(years[0] || '');
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const projected = useMemo(() => {
        const today = new Date();
        if (!monthly) return 0;
        return Math.round((monthly / Math.max(today.getDate(), 1)) * new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate());
    }, [monthly]);

    const averageDaily = Math.round(monthly / Math.max(new Date().getDate(), 1));
    const datasets = { months: monthlyByYear, days: dailyByYear, hours: hourlyByYear };
    const chartData = datasets[mode]?.[year] || [];
    const availableYears = Object.keys(monthlyByYear).sort((a, b) => Number(b) - Number(a));
    const bestPoint = chartData.reduce<{ name: string; pv: number } | null>((best, point) => !best || Number(point.pv) > Number(best.pv) ? point : best, null);
    const yearTotal = (monthlyByYear[year] || []).reduce((sum, point) => sum + Number(point.pv || 0), 0);

    const cards = [
        { label: 'Ten tydzień', value: weekly, note: 'wejść na stronę', icon: TodayRounded, color: '#6ee7b7' },
        { label: 'Ten miesiąc', value: monthly, note: `średnio ${number(averageDaily)} dziennie`, icon: CalendarMonthRounded, color: '#93c5fd' },
        { label: 'Prognoza miesiąca', value: projected, note: 'przy obecnym tempie', icon: TrendingUpRounded, color: '#fde68a' },
        { label: 'Wszystkie wejścia', value: total, note: 'od początku pomiaru', icon: VisibilityRounded, color: '#c4b5fd' },
    ];

    if (loading) return <Box display="flex" alignItems="center" justifyContent="center" minHeight={340}><CircularProgress /></Box>;

    if (dashboard) return <Box>
        <Paper elevation={0} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            minHeight: 74, px: 2.5, py: 1.5, borderRadius: 3, color: '#fff',
            background: 'radial-gradient(circle at 92% 10%, rgba(94,234,212,.25), transparent 34%), linear-gradient(120deg, #172866, #365bc6)'
        }}>
            <Box>
                <Stack direction="row" alignItems="center" gap={1}>
                    <QueryStatsRounded sx={{ color: '#7ce9c3' }} />
                    <Typography fontWeight={950} fontSize="1.05rem">Ruch na stronie</Typography>
                    <Chip label="Dane aktualne" size="small" sx={{ height: 22, color: '#dffdf4', bgcolor: 'rgba(16,185,129,.22)', fontSize: '.66rem', fontWeight: 800 }} />
                </Stack>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.68)' }}>Najważniejsze wyniki serwisu w jednym widoku</Typography>
            </Box>
            <Box textAlign="right">
                <Typography variant="caption" sx={{ color: '#b9c7fb', fontWeight: 800 }}>WEJŚCIA W {year || 'ROKU'}</Typography>
                <Typography variant="h5" fontWeight={950} lineHeight={1}>{number(yearTotal)}</Typography>
            </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1, mt: 1 }}>
            {cards.map(({ label, value, note, icon: Icon, color }) => <Card key={label} elevation={0} sx={{ p: 1.35, minWidth: 0, border: '1px solid #e3e8f2', borderRadius: 2.5, background: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box minWidth={0}>
                        <Typography display="block" noWrap variant="caption" color="text.secondary" fontWeight={850} fontSize=".63rem">{label.toUpperCase()}</Typography>
                        <Typography variant="h5" fontWeight={950} color="#17213d" mt={.25}>{number(value)}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', flex: '0 0 auto', placeItems: 'center', width: 32, height: 32, borderRadius: 2, color: '#17245d', bgcolor: color }}><Icon sx={{ fontSize: 18 }} /></Box>
                </Stack>
                <Typography noWrap variant="caption" color="text.secondary" display="block" mt={.25}>{note}</Typography>
            </Card>)}
        </Box>

        <Paper variant="outlined" sx={{ p: 1.75, mt: 1, borderRadius: 3, borderColor: '#e3e8f2' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                <Box>
                    <Typography fontWeight={900}>Rozkład odwiedzin</Typography>
                    <Typography variant="caption" color="text.secondary">{bestPoint ? `Najwyższy wynik: ${bestPoint.name} · ${number(bestPoint.pv)}` : 'Dane według wybranego zakresu'}</Typography>
                </Box>
                <Stack direction="row" gap={.5}>
                    {([['months', 'Miesiące'], ['days', 'Dni'], ['hours', 'Godziny']] as [ViewMode, string][]).map(([key, label]) => <Button key={key} size="small" variant={mode === key ? 'contained' : 'outlined'} onClick={() => setMode(key)} sx={{ minWidth: 0, px: 1.1, py: .4, fontSize: '.68rem' }}>{label}</Button>)}
                </Stack>
            </Stack>
            {chartData.length ? <ResponsiveContainer width="100%" height={220}>
                {mode === 'months' ? <AreaChart data={chartData} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                    <defs><linearGradient id="ufoDashboardArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#315dca" stopOpacity={.42} /><stop offset="100%" stopColor="#315dca" stopOpacity={.02} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="4 7" stroke="#e8ecf4" /><XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={11} /><YAxis axisLine={false} tickLine={false} fontSize={11} /><Tooltip contentStyle={{ borderRadius: 10, borderColor: '#dfe5f1' }} /><Area type="monotone" dataKey="pv" name="Wejścia" stroke="#315dca" strokeWidth={3} fill="url(#ufoDashboardArea)" />
                </AreaChart> : <BarChart data={chartData} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="4 7" stroke="#e8ecf4" /><XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={11} /><YAxis axisLine={false} tickLine={false} fontSize={11} /><Tooltip contentStyle={{ borderRadius: 10, borderColor: '#dfe5f1' }} /><Bar dataKey="pv" name="Wejścia" fill="#315dca" radius={[6, 6, 0, 0]} />
                </BarChart>}
            </ResponsiveContainer> : <Box display="grid" height={220} sx={{ placeItems: 'center', color: 'text.secondary' }}>Brak danych dla wybranego zakresu.</Box>}
        </Paper>
    </Box>;

    return <Box sx={{ mt: dashboard ? 3 : 0 }}>
        <Paper elevation={0} sx={{
            position: 'relative', overflow: 'hidden', p: { xs: 2.5, md: 4 }, borderRadius: 4,
            color: '#fff', background: 'radial-gradient(circle at 90% 0%, rgba(94,234,212,.28), transparent 32%), linear-gradient(125deg, #111d52 0%, #243d9d 58%, #315dca 100%)'
        }}>
            <Box sx={{ position: 'absolute', right: -70, bottom: -100, width: 310, height: 310, borderRadius: '50%', border: '52px solid rgba(255,255,255,.055)' }} />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={3} position="relative">
                <Box>
                    <Chip icon={<QueryStatsRounded />} label="Dane aktualne" size="small" sx={{ color: '#dffdf4', bgcolor: 'rgba(16,185,129,.2)', fontWeight: 800, '& .MuiChip-icon': { color: '#6ee7b7' } }} />
                    <Typography variant="overline" display="block" mt={2.5} sx={{ color: '#aebeff', fontWeight: 900, letterSpacing: '.14em' }}>ANALITYKA UFO</Typography>
                    <Typography variant="h3" fontWeight={950} letterSpacing="-.045em">Ruch na stronie</Typography>
                    <Typography mt={1} sx={{ color: 'rgba(255,255,255,.72)', maxWidth: 620 }}>Najważniejsze wyniki i rozkład odwiedzin w jednym miejscu.</Typography>
                </Box>
                <Box sx={{ minWidth: 210, alignSelf: { md: 'center' } }}>
                    <Typography variant="caption" sx={{ color: '#aebeff', fontWeight: 800 }}>WEJŚCIA W {year || 'ROKU'}</Typography>
                    <Typography variant="h2" fontWeight={950} lineHeight={1.05}>{number(yearTotal)}</Typography>
                </Box>
            </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1.5, mt: 1.5 }}>
            {cards.map(({ label, value, note, icon: Icon, color }) => <Card key={label} elevation={0} sx={{ p: 2.25, border: '1px solid #e3e8f2', borderRadius: 3, background: 'linear-gradient(145deg, #fff, #f8faff)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box><Typography variant="caption" color="text.secondary" fontWeight={850}>{label.toUpperCase()}</Typography><Typography variant="h4" fontWeight={950} color="#17213d" mt={.5}>{number(value)}</Typography></Box>
                    <Box sx={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 2.5, color: '#17245d', bgcolor: color }}><Icon /></Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={1}>{note}</Typography>
            </Card>)}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 2.4fr) minmax(250px, .8fr)' }, gap: 1.5, mt: 1.5 }}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, borderColor: '#e3e8f2' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} mb={3}>
                    <Box><Typography variant="h6" fontWeight={900}>Rozkład odwiedzin</Typography><Typography variant="body2" color="text.secondary">Porównaj miesiące, dni tygodnia i godziny.</Typography></Box>
                    <Stack direction="row" gap={.75} flexWrap="wrap">
                        {([['months', 'Miesiące'], ['days', 'Dni'], ['hours', 'Godziny']] as [ViewMode, string][]).map(([key, label]) => <Button key={key} size="small" variant={mode === key ? 'contained' : 'outlined'} onClick={() => setMode(key)}>{label}</Button>)}
                    </Stack>
                </Stack>
                {chartData.length ? <ResponsiveContainer width="100%" height={330}>
                    {mode === 'months' ? <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                        <defs><linearGradient id="ufoArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#315dca" stopOpacity={.48} /><stop offset="100%" stopColor="#315dca" stopOpacity={.02} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="4 7" stroke="#e8ecf4" /><XAxis dataKey="name" axisLine={false} tickLine={false} /><YAxis axisLine={false} tickLine={false} /><Tooltip contentStyle={{ borderRadius: 12, borderColor: '#dfe5f1' }} /><Area type="monotone" dataKey="pv" name="Wejścia" stroke="#315dca" strokeWidth={3} fill="url(#ufoArea)" />
                    </AreaChart> : <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid vertical={false} strokeDasharray="4 7" stroke="#e8ecf4" /><XAxis dataKey="name" axisLine={false} tickLine={false} /><YAxis axisLine={false} tickLine={false} /><Tooltip contentStyle={{ borderRadius: 12, borderColor: '#dfe5f1' }} /><Bar dataKey="pv" name="Wejścia" fill="#315dca" radius={[7, 7, 0, 0]} />
                    </BarChart>}
                </ResponsiveContainer> : <Box display="grid" minHeight={330} sx={{ placeItems: 'center', color: 'text.secondary' }}>Brak danych dla wybranego zakresu.</Box>}
                <Stack direction="row" gap={.75} justifyContent="center" flexWrap="wrap" mt={1}>
                    {availableYears.map((item) => <Chip key={item} label={item} clickable color={year === item ? 'primary' : 'default'} variant={year === item ? 'filled' : 'outlined'} onClick={() => setYear(item)} />)}
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: '#e3e8f2', background: 'linear-gradient(155deg, #fbfcff, #f0f4ff)' }}>
                <Box sx={{ display: 'grid', placeItems: 'center', width: 50, height: 50, borderRadius: 3, color: '#fff', background: 'linear-gradient(135deg, #263f9d, #4f76df)' }}><AutoGraphRounded /></Box>
                <Typography variant="overline" display="block" mt={3} color="text.secondary" fontWeight={900}>NAJMOCNIEJSZY PUNKT</Typography>
                <Typography variant="h5" fontWeight={950} color="#17213d">{bestPoint?.name || 'Brak danych'}</Typography>
                <Typography variant="h3" fontWeight={950} color="#315dca" mt={1}>{bestPoint ? number(bestPoint.pv) : '—'}</Typography>
                <Typography color="text.secondary">wejść w wybranym zestawieniu</Typography>
                <Box mt={4} pt={2.5} borderTop="1px solid #dfe5f1">
                    <Typography variant="body2" color="text.secondary">Prognoza jest liczona na podstawie średniej dziennej z bieżącego miesiąca.</Typography>
                </Box>
            </Paper>
        </Box>
    </Box>;
};

export default Charts;
